import React from 'react';
import { Tooltip } from "@mui/material";
import { prepareDragData } from './CalendarHelpers';
import PoolIcon from '@mui/icons-material/Pool'; 
import WavesIcon from '@mui/icons-material/Waves';

function CalendarCell({
  date,
  hotel,
  status,
  styleInfo,
  onDragOver,
  onDragLeave,
  onDrop,
  handleDragStart,
  clickHandler,
}) {
  // Determine if this cell has multiple reservations (collision)
  const hasMultipleReservations = status.reservationCount > 1;
  const collisionClass = hasMultipleReservations ? "colliding" : "";
  
  // Check if any actions are unassigned
  const hasUnassignedActions = status.actions && 
    status.actions.some(action => !action.assigned_to || action.assigned_to.length === 0);

  // Check if any actions are pending (no action_option)
  const hasPendingActions = status.actions &&
    status.actions.some(action => !action.action_option);

  // Count pending actions
  const pendingActionsCount = status.actions ? 
    status.actions.filter(action => !action.action_option).length : 0;

  // Extract regular and pending actions for separate handling
  const regularActions = status.actions ? 
    status.actions.filter(action => action.action_option) : [];
  
  const pendingActions = status.actions ?
    status.actions.filter(action => !action.action_option) : [];

  // Handle pool actions
  const poolActions = status.poolActions || [];
  const hasPoolActions = poolActions.length > 0;

  // Handle drag start with awareness of pending vs regular actions
  const onDragStartHandler = (e, date, hotelId, action) => {
    // Use the helper function to prepare drag data
    const dragData = prepareDragData(action, date, hotelId);
    
    // Set the drag data
    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
    
    // Add class for visual feedback during drag
    e.currentTarget.classList.add('dragging');
    
    // Add pending class if needed
    if (!action?.action_option) {
      e.currentTarget.classList.add('dragging-pending');
    }
    
    // Call the parent handler if provided
    if (handleDragStart) {
      handleDragStart(e, date, hotelId, action);
    }
  };

  // Add debugging to verify what's coming in
  console.log('Cell for date:', date.toISOString().split('T')[0], 
    'has pool actions:', status.poolActions?.length || 0, 
    status.poolActions);

  return (
    <td
      key={date.toISOString()}
      className={`${status.booked ? "booked" : ""} ${styleInfo.className || ""} ${collisionClass} ${hasPendingActions ? 'has-pending' : ''} calendar-cell`} 
      style={{
        ...styleInfo,
        // Add clear visual indicator for cells with pool actions for debugging
        outlineColor: hasPoolActions ? 'red' : undefined,
        outlineStyle: hasPoolActions ? 'solid' : undefined,
        outlineWidth: hasPoolActions ? '2px' : undefined
      }}
      onDragOver={(e) => onDragOver(e)}
      onDragLeave={(e) => onDragLeave && onDragLeave(e)}
      onDrop={(e) => onDrop(e, date, hotel._id)}
      onClick={(e) => {
        clickHandler(date, hotel, status.actions, e, status.reservations);
      }}
      data-has-pool-actions={hasPoolActions ? "true" : "false"}
      data-pool-actions-count={poolActions?.length || 0}
    >
      {/* Enhanced debug view for pool actions */}
      {hasPoolActions && (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 188, 212, 0.2)',

        }}>
          {/* <Tooltip
            title={`${poolActions.length} Pool Action${poolActions.length !== 1 ? 's' : ''} on ${date.toLocaleDateString()}`}
            placement="top"
            arrow
          >
            <div className="pool-wave-indicator" style={{
              position: 'absolute',
              top: 2,
              left: 2,
              background: 'rgba(0, 188, 212, 0.8)',
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}>
              <WavesIcon style={{color: 'white', fontSize: 16}} />
            </div>
          </Tooltip> */}
          {/* <div style={{
            position: 'absolute',
            top: 2,
            right: 2,
            background: '#00bcd4',
            borderRadius: '50%',
            width: 18,
            height: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: 11,
            zIndex: 10
          }}>
            {poolActions.length}
          </div> */}
        </div>
      )}
      
      {/* Show reservation count if multiple reservations */}
      {status.reservationCount > 1 && (
        <div className="reservation-count">
          {status.reservationCount}
        </div>
      )}
      
      {/* Place action count outside of the action div for full opacity */}
      {status.actions && status.actions.length >= 2 && (
        <div className="actions-count-wrapper">
          <div className={`actions-count ${hasPendingActions ? 'pending' : ''}`}>
            {status.actions.length}
            {pendingActionsCount > 0 && ` (${pendingActionsCount}?)`}
          </div>
        </div>
      )}
      
      {/* Pool actions counter - Keep this existing indicator as well */}
      {hasPoolActions && (
        <Tooltip
          title={`${poolActions.length} Pool Maintenance ${poolActions.length > 1 ? 'Actions' : 'Action'}`}
          placement="top"
          arrow
        >
          <div className="pool-action-indicator">
            <PoolIcon fontSize="small" style={{ color: '#00bcd4' }} />
          </div>
        </Tooltip>
      )}
      
      {/* Single action styling for both regular and pending actions */}
      {status.actions &&
        status.actions.length < 2 &&
        status.actions.map((action) => (
          <Tooltip
            title={action.action_option ? (action.name || "Unknown Action") : "Pending Action (No Type Selected)"}
            placement="top"
            arrow
            key={action._id}
            style={{ cursor: "pointer" }}
          >
            <div
              key={action._id}
              className={`cell-action single-action ${(!action.assigned_to || action.assigned_to.length === 0) ? 'unassigned' : ''} ${!action.action_option ? 'pending' : ''}`}
              draggable="true"
              onDragStart={(e) => onDragStartHandler(e, date, hotel._id, action)}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: !action.action_option ? 'transparent' : (action.color || "#3498db"),
                opacity: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer"
              }}
            >
              {!action.action_option && (
                <div className="pending-icon">?</div>
              )}
            </div>
          </Tooltip>
        ))}
      
      {/* Multiple actions styling with special handling for pending */}
      {status.actions && status.actions.length >= 2 && (
        <Tooltip
          title={status.actions.map((action) => 
            action.action_option ? (action.name || "Unknown Action") : "Pending Action (No Type Selected)"
          ).join(", ")}
          placement="top"
          arrow
        >
          <div
            className={`cell-action multiple-actions ${hasUnassignedActions ? 'unassigned' : ''} ${hasPendingActions ? 'pending' : ''}`}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundImage: hasPendingActions ? 'none' : `linear-gradient(to right, ${status.actions
                .filter(action => action.action_option) // Only include actions with action_option
                .map((action) => action.color || "#3498db")
                .join(", ")})`,
              opacity: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1
            }}
            draggable="true"
            onDragStart={(e) => onDragStartHandler(e, date, hotel._id, status.actions[0])} // Pass first action for multiple
          >
            {/* Action count is outside of this div */}
            {hasPendingActions && (
              <div className="pending-icon">?</div>
            )}
          </div>
        </Tooltip>
      )}
        
      {/* Add a turnover indicator if there's a collision */}
      {status.reservationCount > 1 && (
        <div className="turnover-indicator">
          <div className="turnover-arrow"></div>
        </div>
      )}
    </td>
  );
}

export default CalendarCell;
